import { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { MsalInstanceContext } from "../../auth/AppProvider";
import { ThemeContext } from "../../App";
import { HubspotAccountContext } from "../../App";
import { ClipboardDocumentListIcon, CheckBadgeIcon, CreditCardIcon, FlagIcon } from "@heroicons/react/24/outline";
import * as Icons from '@heroicons/react/24/outline';

function WelcomeHomePage({ t, canCreateNewCompletion }) {
  const navigate = useNavigate();
  const msalInstance = useContext(MsalInstanceContext);
  const theme = useContext(ThemeContext);
  const [isVisible, setIsVisible] = useState(false);
  const profileHubspot = useContext(HubspotAccountContext);

  const firstName = profileHubspot.firstname ??
    msalInstance?.getAllAccounts()?.[0]?.name?.split(" ")?.[0] ?? "";

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 250);
    return () => clearTimeout(timer);
  }, []);

  const defaultSteps = [
    {
      number: "1",
      icon: ClipboardDocumentListIcon,
      title: "Complétez votre profil",
      description:
        "État civil, coordonnées, cursus et documents — une seule fois.",
    },
    {
      number: "2",
      icon: CheckBadgeIcon,
      title: "Candidatez en quelques minutes",
      description: "Chaque formulaire est pré-rempli depuis votre profil.",
    },
    {
      number: "3",
      icon: CreditCardIcon,
      title: "Suivez et finalisez",
      description:
        "Validation du dossier, puis inscription et paiement en ligne.",
    },
    {
      number: "4",
      icon: FlagIcon,
      title: "Finalisation",
      description:
        "Finalisez votre dossier de candidature et recevez la confirmation.",
    },
  ];

  const steps = theme?.steps || defaultSteps;

  return (
    <div
      className={
        isVisible
          ? "my-forms-visible layout h-full overflow-x-hidden"
          : "hidden"
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-xs font-semibold tracking-widest uppercase color-primary-perso mb-2">
          {t("BIENVENUE SUR VOTRE ESPACE")}
        </p>
        <h1 className="text-[28px] font-bold text-gray-900 mb-3">
          {t("Bonjour")} {firstName} 👋
        </h1>
        <p className="text-gray-500 mb-8 leading-relaxed">
          {t(
            "Exploration des formations, dépôt de candidatures et suivi en temps réel. Simplifiez votre parcours vers la réussite.",
          )}
        </p>

        {/* Steps */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-1">{t("Parcours simplifié")}</h2>
          <div className="w-10 h-0.5 mx-auto mb-6" style={{ backgroundColor: "var(--primary-color)" }} />
          <div className="flex flex-col md:flex-row gap-6 items-center">
            {steps.map((s) => {
              const Icon = Icons[s.icon] ?? Icons["ClockIcon"];
              return (
                <div
                  key={s.number}
                  className="relative bg-white border p-6 text-center w-96"
                  style={{
                    borderRadius: "20px",
                    borderColor: "#eceef2",
                    boxShadow: "rgba(16, 20, 34, 0.15) 0px 12px 30px -20px",
                  }}
                >
                  <span
                    className="absolute -top-3 -left-3 inline-flex items-center justify-center w-9 h-9 rounded-md text-xs font-bold text-white"
                    style={{ backgroundColor: "var(--primary-color)" }}
                  >
                    {s.number}
                  </span>
                  <div className="mx-auto flex items-center justify-center bg-gray-100 w-12 h-12 rounded-full mb-3">
                    <Icon className="h-6 w-6 mx-auto text-gray-900" />
                  </div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">
                    {s.title}
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {s.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex justify-center">
          {canCreateNewCompletion && <button
            onClick={() => navigate("/newform")}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "var(--primary-color)", borderRadius: "11px" }}
          >
            {t("Trouver ma formation")} →
          </button>}
        </div>

        {/* AI feature card */}
        {/* <div
                    className="rounded-2xl border p-6 mb-8"
                    style={{
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--primary-color) 6%, white), white)",
                        borderColor: "color-mix(in srgb, var(--primary-color) 20%, white)",
                    }}
                >
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                            <span
                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full mb-3"
                                style={{
                                    backgroundColor: "color-mix(in srgb, var(--primary-color) 12%, white)",
                                    color: "var(--primary-color)",
                                }}
                            >
                                <SparklesIcon className="h-3 w-3" />
                                {t("Le plus malin")}
                            </span>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t("Déposez un document, l'IA remplit votre profil")}
                            </h2>
                            <p className="text-sm text-gray-500 leading-relaxed mb-4">
                                {t("Ajoutez votre pièce d'identité et vos relevés de notes\u00a0: notre IA les analyse et pré-remplit votre état civil et votre cursus. Vous validez, c'est prêt pour toutes vos candidatures.")}
                            </p>
                            <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <DocumentCheckIcon className="h-4 w-4 color-primary-perso" />
                                {t("Analyse intelligente de documents")}
                            </p>
                            <p className="text-xs text-gray-400">
                                {t("Formats PDF, JPG, PNG — vos fichiers restent réutilisables dans chaque candidature.")}
                            </p>
                        </div>
                    </div>
                </div> */}

        {/* CTAs */}
        {/* <div className="flex flex-wrap items-center gap-3">
                    <button
                        // CTA principal: compléter/mettre à jour le profil candidat.
                        onClick={() => navigate("/profile")}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm transition-opacity hover:opacity-90"
                        style={{ backgroundColor: "var(--primary-color)" }}
                    >
                        <span className="h-2 w-2 rounded-full bg-white inline-block" />
                        {t("Compléter mon profil")}
                    </button>
                    <button
                        onClick={() => navigate("/newform")}
                        className="inline-flex items-center gap-1 px-6 py-3 rounded-full border border-gray-200 text-gray-700 font-semibold text-sm hover:border-gray-300 transition-colors bg-white"
                    >
                        {t("Voir les formations")} →
                    </button>
                </div> */}
      </div>
    </div>
  );
}

export default withTranslation()(WelcomeHomePage);
